import { useQueryClient } from '@tanstack/react-query';
import {
  flagChat,
  hideChat,
  markAsUnread,
  removeCaseNumber,
  resolveChat,
  updateChat,
} from '@util/firestore/messages';
import { getOrderByOrderNum } from '@util/firestore/orders';
import { getLinearIssueUrl } from '@util/firestore/support/support.service';
import { logError } from '@util/logError';
import { ADMIN_UID } from '@util/support';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { SupportState, type ChatDocument } from 'models/chat';
import type { PublicUserDocument, UserDocument } from 'models/user';
import { useState } from 'react';

export interface SupportAction {
  id: string;
  label: string;
  category: 'admin' | 'chat' | 'case';
  onClick: () => void | Promise<void>;
  condition: (props: SupportActionContext) => boolean;
  loading?: boolean;
  href?: string;
  target?: string;
  type?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'affirm';
}

export interface SupportActionContext {
  userDoc: UserDocument | null | undefined;
  recipientDoc: PublicUserDocument | null | undefined;
  chat: ChatDocument;
  setChat: (chat: ChatDocument) => void;
  isAdmin: boolean;
  isSupport: boolean;
}

interface UseSupportChatActionsOptions {
  onCreateLinearIssue?: (chatId: string) => void;
}

export const useSupportChatActions = (
  options?: UseSupportChatActionsOptions
) => {
  const { userDoc } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const queryClient = useQueryClient();
  const [loadingActions, setLoadingActions] = useState<Record<string, boolean>>(
    {}
  );

  const isSupport = userDoc?.roles?.support || userDoc?.roles?.admin;
  const isAdmin = userDoc?.roles?.admin;

  const setActionLoading = (actionId: string, loading: boolean) => {
    setLoadingActions((prev) => ({ ...prev, [actionId]: loading }));
  };

  const createActions = (
    actionContext: SupportActionContext
  ): SupportAction[] => {
    const { chat, setChat, recipientDoc } = actionContext;

    return [
      // Admin Actions
      {
        id: 'copy-uid',
        label: 'Copy UID',
        category: 'admin',
        type: 'tertiary',
        condition: ({ isAdmin }) => isAdmin,
        onClick: () => {
          navigator.clipboard.writeText(recipientDoc?.uid ?? '');
          showSuccessToast('Copied to clipboard');
        },
      },
      {
        id: 'open-backend',
        label: 'Open in Backend',
        category: 'admin',
        type: 'tertiary',
        condition: ({ isAdmin }) => isAdmin,
        href: `https://console.firebase.google.com/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}/firestore/data/~2Fmessages~2F${chat.id}`,
        target: '_blank',
        onClick: () => {},
      },

      {
        id: 'copy-for-llm',
        label: 'Copy for LLM',
        category: 'admin',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        onClick: () => {
          const { messages, buyer_id, seller_id } = actionContext.chat;
          const lines = messages.map((msg) => {
            const role =
              msg.uid === buyer_id
                ? 'Buyer'
                : msg.uid === seller_id
                ? 'Seller'
                : 'Agent';
            const content = msg.is_img ? '[image]' : msg.content;
            return `${role}: ${content}`;
          });
          navigator.clipboard.writeText(lines.join('\n'));
          showSuccessToast('Conversation copied for LLM');
        },
      },

      // Chat Actions
      {
        id: 'flag-chat',
        label: chat.flagged ? 'Unflag Chat' : 'Flag Chat',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        onClick: async () => {
          try {
            setActionLoading('flag-chat', true);
            await flagChat(chat.id!, !chat.flagged);

            queryClient.invalidateQueries({
              queryKey: ['adminFlaggedChats'],
            });

            setChat({ ...chat, flagged: !chat.flagged });
            showSuccessToast('Chat flagged');
          } catch (error) {
            showErrorToast('Error flagging chat');
          } finally {
            setActionLoading('flag-chat', false);
          }
        },
      },
      {
        id: 'mark-unread',
        label: 'Mark as Unread',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        loading: loadingActions['mark-unread'],
        onClick: async () => {
          try {
            setActionLoading('mark-unread', true);
            await markAsUnread(chat.id, ADMIN_UID);
            showSuccessToast('Marked as unread');
          } catch (e) {
            showErrorToast('Error marking as unread');
          } finally {
            setActionLoading('mark-unread', false);
          }
        },
      },
      {
        id: 'share-chat',
        label: 'Share Chat Link',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        onClick: () => {
          navigator.clipboard
            .writeText(
              `${window.location.origin}/dashboard/messages/${chat.id}`
            )
            .then(() => {
              showSuccessToast('Copied to clipboard');
            });
        },
      },

      // Case Management Actions
      {
        id: 'resolve-case',
        label: 'Resolve Case',
        category: 'case',
        type: 'secondary',
        condition: ({ isSupport, chat }) =>
          isSupport &&
          !!chat.case_num &&
          ![SupportState.Resolved, SupportState.Cancelled].includes(
            chat.support_state ?? 0
          ),
        loading: loadingActions['resolve-case'],
        onClick: async () => {
          setActionLoading('resolve-case', true);
          try {
            await resolveChat(chat.id!, userDoc?.uid!);
            showSuccessToast('Resolved');
            queryClient.invalidateQueries({
              queryKey: ['adminBuyerChats'],
            });
            queryClient.invalidateQueries({
              queryKey: ['adminSellerChats'],
            });
          } catch (e) {
            showErrorToast((e as Error).message);
          } finally {
            setActionLoading('resolve-case', false);
          }
        },
      },
      {
        id: 'remove-case-number',
        label: 'Remove Case #',
        category: 'case',
        type: 'secondary',
        condition: ({ isAdmin }) => isAdmin,
        loading: loadingActions['remove-case-number'],
        onClick: async () => {
          setActionLoading('remove-case-number', true);
          try {
            await removeCaseNumber(chat.id!, userDoc?.uid!);
            showSuccessToast('Case number removed');
            queryClient.invalidateQueries({
              queryKey: ['adminBuyerChats'],
            });
            queryClient.invalidateQueries({
              queryKey: ['adminSellerChats'],
            });
            queryClient.invalidateQueries({
              queryKey: ['allUnread'],
            });
          } catch (e) {
            showErrorToast((e as Error).message);
          } finally {
            setActionLoading('remove-case-number', false);
          }
        },
      },
      {
        id: 'hide-chat',
        label: 'Hide Chat',
        category: 'case',
        type: 'secondary',
        condition: ({ isSupport }) => isSupport,
        onClick: async () => {
          try {
            setActionLoading('hide-chat', true);
            await hideChat(chat.id!, userDoc?.uid ?? '');

            queryClient.invalidateQueries({
              queryKey: ['adminBuyerChats'],
            });

            queryClient.invalidateQueries({
              queryKey: ['adminSellerChats'],
            });

            showSuccessToast('Chat hidden');
          } catch (error) {
            showErrorToast('Error hiding chat');
          } finally {
            setActionLoading('hide-chat', false);
          }
        },
      },
      {
        id: 'link-order',
        label: 'Link Order',
        category: 'chat',
        type: 'tertiary',
        condition: ({ isSupport }) => isSupport,
        onClick: async () => {
          const order_num = prompt(
            'Enter the order number to link this chat to: '
          );
          if (!order_num) return;
          if (isNaN(+order_num)) {
            showErrorToast('Invalid order number');
            return;
          }
          try {
            setActionLoading('link-order', true);
            const order = await getOrderByOrderNum(+order_num, 'admin');
            if (!order) {
              showErrorToast('Order not found');
              return;
            }
            await updateChat(chat.id!, { order_id: order.id });
            showSuccessToast('Order linked');
          } catch (error) {
            logError(
              error,
              undefined,
              undefined,
              'useSupportChatActions link-order'
            );
            showErrorToast('Error linking order');
          } finally {
            setActionLoading('hide-chat', false);
          }
        },
      },
      {
        id: 'fetch-linear-ticket',
        label: 'Fetch Linear ticket',
        category: 'chat',
        type: 'affirm',
        condition: ({ isSupport }: SupportActionContext) => isSupport,
        loading: loadingActions['fetch-linear-ticket'],
        onClick: async () => {
          try {
            setActionLoading('fetch-linear-ticket', true);
            const issueUrl = await getLinearIssueUrl(chat.id);
            if (issueUrl) {
              window.open(issueUrl, '_blank');
            } else {
              options?.onCreateLinearIssue?.(chat.id);
            }
          } catch (error) {
            showErrorToast(
              (error as Error).message || 'Could not look up Linear ticket'
            );
          } finally {
            setActionLoading('fetch-linear-ticket', false);
          }
        },
      },
    ];
  };

  const getActions = (actionContext: SupportActionContext) => {
    const allActions = createActions(actionContext);
    return allActions.filter((action) => action.condition(actionContext));
  };

  const getActionsByCategory = (actionContext: SupportActionContext) => {
    const actions = getActions(actionContext);
    return {
      admin: actions.filter((action) => action.category === 'admin'),
      chat: actions.filter((action) => action.category === 'chat'),
      case: actions.filter((action) => action.category === 'case'),
    };
  };

  return {
    isSupport,
    isAdmin,
    getActions,
    getActionsByCategory,
    loadingActions,
  };
};
